/**
 * The in-cell LIST dropdown of the SDMS sheet — a drop-in replacement for Univer's own
 * `sheets.dropdown.list` component (@univerjs/sheets-ui `ListDropDown`), registered under the
 * SAME component key so every list data-validation cell (Qualifier, IsReport, the dept chain)
 * opens it.
 *
 * WHY. On the Qualifier column the analyst picks symbols ("B", "BB", "J") whose meaning is not
 * obvious from the symbol alone; G5 shows the definitions only in the Qualifiers popup. This
 * dropdown adds a second column with the dbo.Qualifiers Definition next to each symbol (user
 * request 2026-10-20). Univer's list has no slot for that, so the component is re-stated here —
 * it is ~60 lines and keeps Univer's behaviour and look exactly: the same title, the same chip,
 * the same tick, the same "pick toggles, multi keeps the rest in option order" rule, and the
 * same hide-after-change contract with the dropdown manager.
 *
 * The Definition column appears ONLY on a multi-select list (the Qualifier column is the one
 * multi-select list SDMS builds — sdmsDataValidation `multiple`) and only when at least one
 * option has a definition, so IsReport / dept-chain dropdowns render as before.
 *
 * Display-only: the value written to the cell is still the bare symbol(s).
 */
import { useEffect, useState } from 'react';
import { fetchQualifierDefinitions, type QualifierDef } from './sdmsQualifierNotes';
import './SdmsListDropdown.css';

/** Univer's own key for the list dropdown (sheets-ui `ListDropDown.componentKey`). */
export const UNIVER_LIST_DROPDOWN_KEY = 'sheets.dropdown.list';

interface ListOption { label: string; value: string; color?: string }

interface ListDropdownProps {
  popup: {
    anchorRect?: { left?: number; right?: number } | null;
    extraProps: {
      location: { worksheet?: unknown };
      hideFn: () => void;
      onChange?: (value: string[]) => unknown;
      options: ListOption[];
      defaultValue?: string;
      multiple?: boolean;
    };
  };
}

/** @univerjs/sheets `deserializeListOptions` — a JSON string array, else a comma list. Re-stated
 *  because @univerjs/sheets is a transitive dependency we do not declare. */
function readListValue(value: string | undefined, multiple: boolean | undefined): string[] {
  if (!value) return [];
  if (!multiple) return [value];
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.every(o => typeof o === 'string')) return (parsed as string[]).filter(Boolean);
  } catch { /* not JSON — the comma form OG and the Qualifiers picker write */ }
  return value.split(',').filter(Boolean);
}

/** Dark chip background → white text (Univer uses ColorKit.isDark; a list built by SDMS has no
 *  colours, so this only matters for a template that colours its options). */
function isDarkColor(color: string | undefined): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(color ?? '');
  if (!m?.[1]) return false;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 < 128;
}

export function SdmsListDropdown({ popup: { extraProps, anchorRect } }: ListDropdownProps) {
  const { location, hideFn, onChange, options, defaultValue, multiple } = extraProps;
  const [value, setValue] = useState<string[]>(() => readListValue(defaultValue, multiple));
  const [defs, setDefs] = useState<ReadonlyMap<string, QualifierDef> | null>(null);

  useEffect(() => {
    if (!multiple) return;
    let alive = true;
    void fetchQualifierDefinitions().then(m => { if (alive) setDefs(m); });   // session-cached
    return () => { alive = false; };
  }, [multiple]);

  if (!location.worksheet) return null;

  const definitionOf = (o: ListOption) => defs?.get(o.value.trim().toUpperCase())?.definition?.trim() ?? '';
  const showDefs = !!multiple && !!defs && options.some(o => definitionOf(o) !== '');
  const cellWidth = (anchorRect?.right ?? 0) - (anchorRect?.left ?? 0);

  const pick = async (item: ListOption) => {
    const selected = value.includes(item.value);
    const set = selected
      ? new Set(value.filter(v => v !== item.value))
      : new Set(multiple ? [...value, item.value] : [item.value]);
    const next = options.filter(o => set.has(o.value)).map(o => o.value);   // option order, as Univer
    setValue(next);
    if ((await onChange?.(next)) !== false) hideFn();
  };

  return (
    <div
      data-u-comp="sheets-dropdown-list"
      className={`sld-box univer-box-border univer-rounded-lg univer-bg-white univer-py-1 univer-text-gray-900 univer-shadow${showDefs ? ' sld-box--defs' : ''}`}
      style={showDefs ? { minWidth: Math.max(cellWidth, 360) } : { minWidth: cellWidth, maxWidth: Math.max(cellWidth, 200) }}
    >
      <div className="univer-px-3.5 univer-py-1 univer-pt-2 univer-text-xs">
        {multiple ? 'Select items' : 'Select an item'}
      </div>
      {showDefs && (
        <div className="sld-head">
          <span className="sld-sym">Qualifier</span>
          <span className="sld-def">Definition</span>
          <span className="sld-tick" />
        </div>
      )}
      <div className={`sld-list univer-flex univer-flex-col univer-gap-1 univer-overflow-y-auto univer-px-2 univer-py-1${showDefs ? ' sld-list--defs' : ' univer-max-h-52'}`}>
        {options.map((item, i) => {
          const selected = value.includes(item.value);
          const dark = isDarkColor(item.color);
          const definition = showDefs ? definitionOf(item) : '';
          return (
            <div
              key={i}
              className="sld-row univer-flex univer-cursor-pointer univer-flex-row univer-items-center univer-justify-between univer-rounded-md univer-px-1.5 univer-py-1 hover:univer-bg-gray-50"
              title={definition ? `${item.label}: ${definition}` : undefined}
              onClick={() => { void pick(item); }}
            >
              <span className={showDefs ? 'sld-sym' : undefined}>
                <span
                  className={`univer-inline-flex univer-h-4 univer-w-fit univer-items-center univer-truncate univer-rounded-full univer-px-1.5 univer-text-xs ${dark ? 'univer-text-white' : 'univer-text-gray-900'}`}
                  style={{ background: item.color }}
                >
                  {item.label}
                </span>
              </span>
              {showDefs && <span className="sld-def">{definition}</span>}
              <span className="sld-tick univer-ml-3 univer-size-4 univer-flex-shrink-0 univer-flex-grow-0 univer-text-base">
                {selected && <i className="ti ti-check" aria-label="selected" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
